/* @img-migrated */
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import Img from '../Img';
const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Membership', path: '/membership' },
  { name: 'Events', path: '/events' },
  { name: 'News & Insights', path: '/news' },
  { name: 'Leadership', path: '/leadership' },
  { name: 'Members', path: '/members' },
  { 
    name: 'Resources', 
    children: [
      { name: 'Brand Resources', path: '/brand-resources' },
      { name: 'Marketing Request', path: '/marketing-request' },
    ]
  },
  { name: 'Contact', path: '/contact' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isHome ? 'bg-[#002249] border-b border-white/10 shadow-md' : 'glass border-b border-slate-200'
    }`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
          {/* Logo — loading-version (white frame) on Home for contrast over deep blue; original dark logo on light pages */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="nav-logo">
            <Img variant="generic"
              src={isHome
                ? "/brand/logo-white.png"
                : "/brand/logo-color.png"
              }
              alt="DCCF Logo"
              className="h-10 sm:h-11 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              link.children ? (
                <div 
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => setResourcesOpen(true)}
                  onMouseLeave={() => setResourcesOpen(false)}
                >
                  <button 
                    className={`flex items-center px-3 xl:px-4 py-2 text-[13px] xl:text-sm font-medium transition-colors rounded-md ${
                      isHome ? 'text-white/85 hover:text-white hover:bg-white/10' : 'text-slate-600 hover:text-[#002249] hover:bg-slate-50'
                    }`}
                    data-testid="nav-resources-dropdown"
                  >
                    {link.name}
                    <ChevronDown className="ml-1 w-3.5 h-3.5" />
                  </button>
                  <AnimatePresence>
                    {resourcesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-1 w-52 bg-white rounded-lg shadow-xl border border-slate-100 py-1.5 overflow-hidden"
                      >
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-[#002249] transition-colors"
                            data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 xl:px-4 py-2 text-[13px] xl:text-sm font-medium transition-colors rounded-md ${
                    location.pathname === link.path
                      ? isHome ? 'text-white bg-white/10' : 'text-[#002249] bg-slate-50'
                      : isHome ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-slate-600 hover:text-[#002249] hover:bg-slate-50'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-2.5">
            <Link
              to="/membership"
              className={`px-5 py-2 text-sm font-semibold rounded-md transition-all duration-200 ${
                isHome 
                  ? 'bg-white text-[#002249] hover:bg-white/90 shadow-sm' 
                  : 'bg-[#002249] text-white hover:bg-[#0a3570] shadow-sm'
              }`}
              data-testid="nav-join-btn"
            >
              Join Chamber
            </Link>
            <Link
              to="/member-login"
              className={`px-5 py-2 text-sm font-medium rounded-md border-2 transition-all duration-200 ${
                isHome 
                  ? 'border-white/70 text-white hover:bg-white hover:text-[#002249]' 
                  : 'border-[#002249]/80 text-[#002249] hover:bg-[#002249] hover:text-white'
              }`}
              data-testid="nav-login-btn"
            >
              Login
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors ${
              isHome ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'
            }`}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-white border-t border-slate-100 shadow-xl"
          >
            <div className="px-5 py-5 space-y-1 max-h-[calc(100vh-64px)] overflow-y-auto">
              {navLinks.map((link) => (
                link.children ? (
                  <div key={link.name} className="py-1">
                    <p className="px-3 py-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                      {link.name}
                    </p>
                    {link.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={() => setIsOpen(false)}
                        className="block px-5 py-2.5 text-[15px] text-slate-600 hover:text-[#002249] hover:bg-slate-50 rounded-md transition-colors"
                        data-testid={`mobile-nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`block px-3 py-2.5 text-[15px] font-medium rounded-md transition-colors ${
                      location.pathname === link.path
                        ? 'text-[#002249] bg-[#002249]/5'
                        : 'text-slate-700 hover:text-[#002249] hover:bg-slate-50'
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <div className="pt-4 mt-3 space-y-2.5 border-t border-slate-100">
                <Link
                  to="/membership"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center px-4 py-3 bg-[#002249] text-white font-semibold rounded-md text-[15px]"
                  data-testid="mobile-join-btn"
                >
                  Join Chamber
                </Link>
                <Link
                  to="/member-login"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center px-4 py-3 border-2 border-[#002249] text-[#002249] font-semibold rounded-md text-[15px]"
                  data-testid="mobile-login-btn"
                >
                  Login
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
